import React, { useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { IoCheckmark } from "react-icons/io5";
import { useQueryClient } from "react-query";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import TextTranslate from "../../../utils/TextTranslate";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import { useFetchLanguageList } from "../../../store/api/useFetchLanguageList";
import Cookies from "js-cookie";
import useApiPost from "../../../hooks/PostData";
import { updateSpesificUserData } from "../../../store/Slices/UserSlice";

const groupClass =
  "overflow-hidden rounded-2xl bg-secondary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]";
const divide =
  "[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]";

const SelectLanguageModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { postData } = useApiPost();
  const { data: languageListRes } = useFetchLanguageList();
  const [language_id, setLanguageId] = useState(1);
  const ViewManager = useAppSelector((state) => state.ViewManager);

  const handleCloseModal = () => {
    dispatch(
      updateViewState({
        show_select_language_modal: false,
      }),
    );
  };

  useEffect(() => {
    const tempStatus_id = Cookies.get("language_id");
    setLanguageId(tempStatus_id ? Number(tempStatus_id) : 1);
  }, [ViewManager.show_select_language_modal]);

  const languages =
    languageListRes?.data?.Records?.filter((language) => language.status) ??
    [];

  async function save() {
    const prevId = Number(Cookies.get("language_id") || 0);
    const prev = languages.find((l) => l.language_id == prevId);
    const next = languages.find((l) => l.language_id == language_id);
    Cookies.set("language_id", language_id.toString(), { expires: 365 });
    try {
      await postData("users/updateUser", { language_id });
      dispatch(updateSpesificUserData({ language_id }));
    } catch {
      /* cookie already saved — push locale will sync next launch */
    }

    const alignmentChanged =
      (prev?.language_alignment || "LTR") !==
      (next?.language_alignment || "LTR");

    if (alignmentChanged) {
      location.reload();
      return;
    }

    await queryClient.invalidateQueries(["language/get-language-words"]);
    handleCloseModal();
  }

  return (
    <Dialog
      open={ViewManager.show_select_language_modal}
      onClose={handleCloseModal}
      className="relative z-[100]"
    >
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm">
        <div className="flex min-h-full items-end justify-center p-0 sm:items-center sm:p-4">
          <DialogPanel className="relative flex max-h-[85dvh] w-full max-w-md flex-col overflow-hidden rounded-t-[1.25rem] bg-primary shadow-2xl sm:rounded-2xl">
            <DialogTitle className="shrink-0 border-b border-black/[0.06] px-4 py-3.5 text-center text-[17px] font-semibold tracking-tight text-darkText dark:border-white/[0.08]">
              <TextTranslate text="Language" />
            </DialogTitle>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              <div className={`${groupClass} ${divide}`}>
                {languages.map((e) => {
                  const selected = e.language_id == language_id;
                  return (
                    <button
                      type="button"
                      key={e.language_id}
                      onClick={() => setLanguageId(e.language_id)}
                      className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition active:bg-black/[0.03] dark:active:bg-white/[0.04]"
                    >
                      <span className="min-w-0 flex-1 text-[17px] text-darkText">
                        {e.language}
                      </span>
                      {selected ? (
                        <IoCheckmark className="shrink-0 text-xl text-themeColor" />
                      ) : (
                        <span className="h-5 w-5 shrink-0" aria-hidden />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="shrink-0 border-t border-black/[0.06] bg-primary/95 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl dark:border-white/[0.08]">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-xl bg-black/[0.06] py-3 text-[17px] font-medium text-darkText transition active:scale-[0.99] dark:bg-white/10"
                >
                  <TextTranslate text="Cancel" />
                </button>
                <button
                  type="button"
                  onClick={save}
                  className="rounded-xl bg-themeColor py-3 text-[17px] font-semibold text-themeTextColor transition active:scale-[0.99]"
                >
                  <TextTranslate text="Save" />
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default SelectLanguageModal;
